/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Form, Grid } from '@mairistem/composants';

import type { ActeAdresse, ConventionDeclaration } from '@/types';
import { OPTIONS_TYPE_COMPARUTION } from '@/constants';
import { formatPrenomSaisie } from '@/utils';

import PanelModalEdition from '../PanelModalEdition';
import CommuneDelegueField from './CommuneDelegueField';

interface InformationsEnregistrementDeclarationModalProps {
  signataireNomFamille: string;
  signatairePrenom: string;
  signataireQualite: string;
  statut?: string;
  adressePacs?: ActeAdresse;
  typeComparution?: number | string;
  commentaire?: string;
  updated: boolean;
  onSave: () => Promise<void>;
  onChange: (data: Partial<ConventionDeclaration>) => void;
  onClose: () => void;
  onCancel: () => void;
}

// L'API renvoie le libellé complet (parfois avec une coquille, ex.
// "personelle") plutôt que l'identifiant numérique de OPTIONS_TYPE_COMPARUTION :
// on retrouve l'option correspondante par recoupement de mots plutôt que par
// égalité stricte pour rester tolérant à ce type d'écart.
const normalizeComparutionLabel = (label: string): string[] =>
  label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 3);

const resolveTypeComparutionOption = (
  value?: number | string | null,
): (typeof OPTIONS_TYPE_COMPARUTION)[number] | undefined => {
  if (value == null || value === '') return undefined;

  if (typeof value === 'number') {
    return OPTIONS_TYPE_COMPARUTION.find((option) => option.value === value);
  }

  const valueWords = new Set(normalizeComparutionLabel(value));
  let bestOption: (typeof OPTIONS_TYPE_COMPARUTION)[number] | undefined;
  let bestScore = 0;

  OPTIONS_TYPE_COMPARUTION.forEach((option) => {
    const optionWords = normalizeComparutionLabel(option.text);
    const score = optionWords.filter((word) => valueWords.has(word)).length;
    if (score > bestScore) {
      bestScore = score;
      bestOption = option;
    }
  });

  return bestOption;
};

const InformationsEnregistrementDeclarationModal = (
  props: InformationsEnregistrementDeclarationModalProps,
) => {
  const {
    signataireNomFamille,
    signatairePrenom,
    signataireQualite,
    statut,
    adressePacs,
    typeComparution,
    commentaire,
    updated,
    onSave,
    onChange,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const isDissous = statut === 'Dissous';

  const typeComparutionOption = React.useMemo(
    () => resolveTypeComparutionOption(typeComparution),
    [typeComparution],
  );

  const handleChangeSignataireNomFamille = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({ signataireNomFamille: data.value });
    },
    [onChange],
  );

  const handleChangeSignatairePrenom = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({
        signatairePrenom: formatPrenomSaisie('signatairePrenom', data.value),
      });
    },
    [onChange],
  );

  const handleChangeSignataireQualite = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({ signataireQualite: data.value });
    },
    [onChange],
  );

  const handleChangeTypeComparution = React.useCallback(
    (_e: React.SyntheticEvent, data: { value: number }) => {
      const selected = OPTIONS_TYPE_COMPARUTION.find(
        (option) => option.value === data.value,
      );
      onChange({ typeComparution: (selected?.text ?? data.value) as any });
    },
    [onChange],
  );

  const handleChangeCodePostal = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({ adressePacs: { codePostal: data.value } as any });
    },
    [onChange],
  );

  const handleChangeLocalite = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({ adressePacs: { localite: data.value } as any });
    },
    [onChange],
  );

  const handleChangeLibellePays = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({ adressePacs: { libellePays: data.value } as any });
    },
    [onChange],
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        adressePacs: {
          communeDelegue: libelle,
          estCommuneDelegue: drapeau,
        } as any,
      });
    },
    [onChange],
  );

  const handleChangeCommentaire = React.useCallback(
    (_e: React.FormEvent<HTMLTextAreaElement>, data: { value: string }) => {
      onChange({ commentaire: data.value });
    },
    [onChange],
  );

  const handleSave = React.useCallback(() => {
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="enregistrement-declaration-modal"
      errors={errors}
      loading={loading}
      header="INFORMATIONS D'ENREGISTREMENT DE LA DÉCLARATION"
      updated={updated}
      onSave={handleSave}
      onClose={onCancel}
    >
      <Grid as={Form}>
        <Grid.Row>
          <Grid.Column width={5}>
            <Form.Input
              fluid
              label="Nom"
              value={signataireNomFamille ?? ''}
              input={{
                'data-testid': 'enregistrement-declaration-signataire-nom',
              }}
              onChange={handleChangeSignataireNomFamille}
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <Form.Input
              fluid
              label="Prénom"
              value={signatairePrenom ?? ''}
              input={{
                'data-testid': 'enregistrement-declaration-signataire-prenom',
              }}
              onChange={handleChangeSignatairePrenom}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <Form.Input
              fluid
              label="Grade signataire"
              value={signataireQualite ?? ''}
              input={{
                'data-testid': 'enregistrement-declaration-signataire-grade',
              }}
              onChange={handleChangeSignataireQualite}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={5}>
            <Form.Input
              fluid
              label="Code postal"
              value={adressePacs?.codePostal ?? ''}
              input={{
                'data-testid': 'enregistrement-declaration-code-postal',
              }}
              onChange={handleChangeCodePostal}
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <Form.Input
              fluid
              label="Localité"
              value={adressePacs?.localite ?? ''}
              input={{ 'data-testid': 'enregistrement-declaration-localite' }}
              onChange={handleChangeLocalite}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <Form.Input
              fluid
              label="Pays"
              value={adressePacs?.libellePays ?? ''}
              input={{ 'data-testid': 'enregistrement-declaration-pays' }}
              onChange={handleChangeLibellePays}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <CommuneDelegueField
              data-testid="enregistrement-declaration-commune-delegue"
              libelle={adressePacs?.communeDelegue}
              drapeau={(adressePacs as any)?.estCommuneDelegue}
              onChange={handleChangeCommuneDelegue}
            />
          </Grid.Column>
        </Grid.Row>
        {!isDissous && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Dropdown
                selection
                fluid
                label="Type de comparution"
                options={OPTIONS_TYPE_COMPARUTION}
                value={typeComparutionOption?.value}
                data-testid="enregistrement-declaration-type-comparution"
                onChange={handleChangeTypeComparution}
              />
            </Grid.Column>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Column width={16}>
            <Form.TextArea
              label="Commentaire"
              rows={2}
              value={commentaire ?? ''}
              data-testid="enregistrement-declaration-commentaire"
              onChange={handleChangeCommentaire}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </PanelModalEdition>
  );
};

export default InformationsEnregistrementDeclarationModal;
